import { defineStore } from 'pinia';
import axios from 'axios';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';

export const useAuthStore = defineStore('auth', {
	state: () => ({
		token: localStorage.getItem('auth_token') || '',
		user: null,
		initialized: false,
		initializing: false,
		lastError: null,
	}),

	getters: {
		isAuthenticated: (state) => Boolean(state.token),
		requestHeaders: (state) => state.token
				? { headers: { Authorization: `Bearer ${state.token}` } }
				: {},
	},

	actions: {
		persistToken(token, remember) {
			this.token = token;

			if (remember) {
				localStorage.setItem('auth_token', token);
			} else {
				localStorage.removeItem('auth_token');
			}
		},

		clearSession() {
			this.token = '';
			this.user = null;
			localStorage.removeItem('auth_token');
		},

		async login({ email, password, remember = false }) {
			try {
				this.lastError = null;

				const response = await axios.post(`${STRAPI_URL}/api/auth/local`, {
					identifier: email,
					password,
				});

				const { jwt, user } = response.data;

				this.persistToken(jwt, remember);
				this.user = user;

				return { success: true };
			} catch (error) {
				const message = error.response?.data?.error?.message || 'Login failed';

				this.lastError = message;

				return { success: false, error: message };
			}
		},

		async fetchMe() {
			if (!this.token) {
				return { success: false, error: 'Missing token' };
			}

			try {
				const response = await axios.get(
					`${STRAPI_URL}/api/users/me`,
					this.requestHeaders
				);

				this.user = response.data;

				return { success: true, user: this.user };
			} catch (error) {
				const message =
					error.response?.data?.error?.message || 'Failed to validate session';

				this.lastError = message;
				this.clearSession();

				return { success: false, error: message };
			}
		},

		async initialize() {
			if (this.initialized || this.initializing) {
				return;
			}

			this.initializing = true;

			try {
				if (this.token) {
					await this.fetchMe();
				}
			} finally {
				this.initialized = true;
				this.initializing = false;
			}
		},

		logout() {
			this.clearSession();
		},
	},
});
